import Button from 'tailwind-components/Button/Button';
import CustomersTable from './CustomersTable/CustomersTable';
import CustomersTableFilters from './CustomersTableFilters/CustomersTableFilters';
import Modal from 'tailwind-components/Modal/Modal';
import PotentialMatchesModal from './Modals/PotentialMatchesModal';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import icons from 'constants/icons';
import style from './DashboardCustomer.module.scss';
import useCustomersData from 'hooks/useCustomersData';
import useDebounce from 'hooks/useDebounce';
import { Copy } from 'lucide-react';
import { isDataEqual } from 'helpers/arrays';
import { useDispatch } from 'react-redux';
import { emailedCarrierPolicy, ignorePolicy } from 'state/actions/healthSherpa';
import { showAlert } from 'state/actions/site';

import {
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField
} from '@mui/material';

const SearchIcon = icons['search'];
const ArrowRight = icons['arrowRight'];

export default function DashboardCustomers({
  setLocalLoader,
  getAppointmentsViewData,
  createAppointmentRequest,
  carriers,
  user
}) {
  const dispatch = useDispatch();

  // State
  const [showFilters, setShowFilters] = React.useState(false);
  const [filterCount, setFilterCount] = useState(1);
  const [loadingApptsData, setLoadingApptsData] = useState(true);
  const [bobActionModalData, setBobActionModalData] = useState({});
  const [activePanel, setActivePanel] = useState({});
  const [isSearching, setIsSearching] = useState(true);
  const [selectedFilterName, setSelectedFilterName] = useState('');

  // Refs
  const inputRef = useRef(null);

  const cancelTokenSource = axios.CancelToken.source();

  const {
    data_for_table,
    agent_policies_count,
    currentPage,
    setCurrentPage,
    setCurrentPageSize,
    formData,
    clearFilters,
    handleChangeSelectValue,
    applyFilters,
    setformData,
    setTriggerFetch,
    setPotentialMatchesData,
    potentialMatchesData,
    loadingPotentialMatches,
    deleteBobMatch,
    getPotentialMatches,
    confirmBobMatch,
    selectedFilterData,
    setSelectedFilterData,
    potentialMatchesCount
  } = useCustomersData({
    loadingApptsData,
    setLocalLoader,
    setFilterCount,
    setShowFilters,
    setActivePanel
  });

  const hasUnsavedFilters = useMemo(
    () => formData && !isDataEqual(selectedFilterData, formData),
    [formData, selectedFilterData]
  );

  useEffect(() => {
    getAppointmentsViewData(() => {}, cancelTokenSource, true);
    setLoadingApptsData(false);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  const debouncedSearch = useDebounce(formData.searchTerm, 800);

  useEffect(() => {
    if (isSearching) return;
    applyFilters();
    setIsSearching(true);
  }, [debouncedSearch]);

  useEffect(() => {
    setIsSearching(false);
  }, [data_for_table]);

  useEffect(() => {
    if (inputRef.current) {
      if (!isSearching) inputRef.current.focus();
      else inputRef.current.blur();
    }
  }, [isSearching]);

  const handleClosePotentialMatchesModal = () =>
    setPotentialMatchesData({
      HsPolicyId: '',
      currentBobMatchId: '',
      npn: '',
      BobStatus: '',
      potentialBobMatches: []
    });

  const handleCloseModalBob = () => setBobActionModalData({});

  const copyText = text => {
    navigator.clipboard.writeText(text);
    dispatch(showAlert({ message: 'Copied to clipboard!', code: 'success' }));
  };

  return (
    <>
      <Modal
        active={bobActionModalData.id}
        title='BOB Action'
        footer={
          <div
            className={style.wrapButtonsSmall}
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '15px',
              justifyContent: 'space-between'
            }}
          >
            <Button
              fill
              variant='secondary'
              text='Cancel'
              onClick={handleCloseModalBob}
            />
            <Button
              fill
              variant={bobActionModalData.ignored ? 'primary' : 'danger'}
              isDisabled={bobActionModalData.status === 'EmailedCarrier'}
              text={
                bobActionModalData.ignored
                  ? 'Un-Ignore Policy'
                  : 'Ignore Policy'
              }
              onClick={() =>
                dispatch(
                  ignorePolicy(
                    function loader(val) {
                      setLocalLoader(val);
                      applyFilters();
                      handleCloseModalBob();
                    },
                    () => {},
                    bobActionModalData.id,
                    {
                      ignore_bob: true,
                      npn: bobActionModalData.AssistorNpn
                    },
                    bobActionModalData.ignored
                  )
                )
              }
            />
            <Button
              fill
              className={style.buttonNoWrap}
              isDisabled={
                !(
                  bobActionModalData.email !== 'Unavailable' &&
                  (bobActionModalData.status === 'PendingMatch' ||
                    bobActionModalData.status === 'AssignedToOther')
                )
              }
              variant='primaryDarkened'
              text='Mark as Sent'
              onClick={() => {
                dispatch(
                  emailedCarrierPolicy(
                    function loader(val) {
                      setLocalLoader(val);
                      applyFilters();
                      handleCloseModalBob();
                    },
                    () => {},
                    {
                      policy_id: bobActionModalData.id,
                      carrier: bobActionModalData.issuer_name,
                      name: bobActionModalData.AssistorName.trim(),
                      npn: bobActionModalData.AssistorNpn,
                      carrierEmail: bobActionModalData.email
                    }
                  )
                );
              }}
            />
          </div>
        }
        closeModal={handleCloseModalBob}
      >
        <div
          style={{
            minHeight: '280px'
          }}
        >
          <div
            style={{
              borderTop: '2px solid #f6f6f6',
              marginBlock: '10px'
            }}
          />
          <InputLabel htmlFor='outlined-adornment-email'>
            Carrier Email:
          </InputLabel>
          <OutlinedInput
            id='outlined-adornment-email'
            value={bobActionModalData.email}
            sx={{
              width: '100%',
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000000'
              }
            }}
            disabled
            endAdornment={
              <InputAdornment position='start'>
                <Button
                  variant='transparent'
                  onClick={() => {
                    copyText(bobActionModalData.email);
                  }}
                  icon={<Copy className='size-inherit' />}
                />
              </InputAdornment>
            }
            slotProps={{
              input: {
                readOnly: true
              }
            }}
          />
          <InputLabel
            htmlFor='outlined-adornment-message'
            style={{
              marginTop: '20px'
            }}
          >
            Message:
          </InputLabel>
          <OutlinedInput
            multiline
            id='outlined-adornment-message'
            value={bobActionModalData.message}
            sx={{
              width: '100%',
              fontSize: '12px',
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000000'
              }
            }}
            disabled
            rows={8}
            endAdornment={
              <InputAdornment position='start'>
                <Button
                  variant='transparent'
                  onClick={() => copyText(bobActionModalData.message)}
                  icon={<Copy className='size-inherit' />}
                />
              </InputAdornment>
            }
            slotProps={{
              input: {
                readOnly: true
              }
            }}
          />
        </div>
      </Modal>
      <PotentialMatchesModal
        potentialMatchesData={potentialMatchesData}
        handleClosePotentialMatchesModal={handleClosePotentialMatchesModal}
        loadingPotentialMatches={loadingPotentialMatches}
        deleteBobMatch={deleteBobMatch}
        confirmBobMatch={confirmBobMatch}
      />
      <Grid
        container
        columnSpacing={{ xs: 1 }}
        rowSpacing={{ xs: 1 }}
        marginTop={2}
        paddingTop={1}
      >
        <Grid
          item
          xs={showFilters ? 12 : 0}
          lg={showFilters ? 4 : 0}
          xl={showFilters ? 3 : 0}
          className={style.filtersContainerMain}
          data-tour='submissions-filters'
        >
          <CustomersTableFilters
            filterCount={filterCount}
            setFilterCount={setFilterCount}
            formData={formData}
            setformData={setformData}
            clearFilters={clearFilters}
            handleChangeSelectValue={handleChangeSelectValue}
            applyFilters={applyFilters}
            showFilters={showFilters}
            setSelectedFilterData={setSelectedFilterData}
            hasUnsavedFilters={hasUnsavedFilters}
            setSelectedFilterName={setSelectedFilterName}
          />
        </Grid>
        <Grid item xs={12} lg={showFilters ? 8 : 12} xl={showFilters ? 9 : 12}>
          <div className={style.tableContainer}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 15px 0px',
                fontSize: '14px',
                fontStyle: 'italic'
              }}
            >
              {selectedFilterName && (
                <p style={{ margin: 0 }}>
                  Selected Filter: {selectedFilterName}
                </p>
              )}
            </div>
            <Grid
              container
              padding={2}
              rowSpacing={{ xs: 1 }}
              data-tour='submissions-toolbar'
            >
              <Grid item xs={12} sm={4}>
                <Button
                  text={`${
                    showFilters ? 'Hide Filters' : 'Show Filters'
                  } ${`(${filterCount})`}`}
                  size='medium'
                  className={style.buttonNoWrap}
                  variant='primaryDarkened'
                  data-tour='submissions-show-filters'
                  icon={
                    <ArrowRight
                      className={
                        showFilters
                          ? style.arrowRightTransitionActive
                          : style.arrowRightTransition
                      }
                    />
                  }
                  onClick={() => {
                    setShowFilters(!showFilters);
                    if (showFilters && hasUnsavedFilters) {
                      applyFilters();
                      setSelectedFilterData(formData);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={8} display='flex'>
                <TextField
                  inputRef={inputRef}
                  disabled={isSearching}
                  fullWidth
                  size='small'
                  id='outlined-search'
                  label='Search by client name, policy number, phone, or email'
                  placeholder='Enter client name, policy number, phone, or email...'
                  value={formData.searchTerm}
                  onChange={e => {
                    if (!isSearching) {
                      setformData({
                        ...formData,
                        searchTerm: e.target.value
                      });
                    }
                  }}
                  type='search'
                  InputProps={{
                    sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                  }}
                />
                <Button
                  text='Search'
                  variant='primary'
                  size='medium'
                  className={style.searchButton}
                  onClick={applyFilters}
                  icon={<SearchIcon />}
                />
              </Grid>
            </Grid>
            <CustomersTable
              data_for_table={data_for_table}
              agent_policies_count={agent_policies_count}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setCurrentPageSize={setCurrentPageSize}
              formData={formData}
              carriers={carriers}
              user={user}
              setBobActionModalData={setBobActionModalData}
              setTriggerFetch={setTriggerFetch}
              setPotentialMatchesData={setPotentialMatchesData}
              getPotentialMatches={getPotentialMatches}
              activePanel={activePanel}
              setActivePanel={setActivePanel}
              handleChangeSelectValue={handleChangeSelectValue}
              applyFilters={applyFilters}
              setLocalLoader={setLocalLoader}
              createAppointmentRequest={createAppointmentRequest}
              selectedFilterData={selectedFilterData}
              potentialMatchesCount={potentialMatchesCount}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}

DashboardCustomers.propTypes = {
  setLocalLoader: PropTypes.func,
  getAppointmentsViewData: PropTypes.func,
  createAppointmentRequest: PropTypes.func,
  carriers: PropTypes.object,
  user: PropTypes.object
};
